import React, { useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Spin, message } from "antd";
import axios from "axios";
import AccountCreate from "../../components/AccountCreate/AccountCreate";
import { checkIsLoaded } from "../../lib/utils/checkLoadingStatus";
import {
  fetchAccountStatuses,
  fetchCountries,
  fetchRegions,
  getDocTypes,
  fetchPhoneTypes,
  fetchAccountTypes,
} from "../../store/slices/dicts";
import { createAccountRoute } from "../../lib/routes/accounts";
import { toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import { hasLocalAccess } from "../../lib/configs";
import { useAppSelector } from "../..";
import { AnyObject } from "../../lib/interfaces/AnyObject";
import { fetchOrganizationPropertyTypes } from "../../store/slices/property-type-admin";
import { useLoading } from "../../lib/hooks/useLoading";

function AccountCreateContainer() {
  const dispatch = useDispatch();

  const {
    accountStatusesLoading,
    countriesLoading,
    regionsLoading,
    docTypesLoading,
    phoneTypesLoading,
    accountTypesLoading,
  } = useAppSelector((state) => state.dicts);

  const { organizationPropertyTypesLoading } = useAppSelector((state) => state.propertyTypesAdmin);

  const { tenantPropertiesLoading, tenantPropertiesDto } = useSelector(
    (state: RootStateOrAny) => state.tenants
  );

  const history = useHistory();

  const neededProperties = tenantPropertiesDto
    .filter((item) => item.propertyType.alias === "client.company_attributes")
    .map((item) => item.stringValue);

  useEffect(() => {
    if (!checkIsLoaded(accountStatusesLoading)) dispatch(fetchAccountStatuses());
    if (!checkIsLoaded(countriesLoading)) dispatch(fetchCountries());
    if (!checkIsLoaded(regionsLoading)) dispatch(fetchRegions());
    if (!checkIsLoaded(organizationPropertyTypesLoading))
      dispatch(fetchOrganizationPropertyTypes());
    if (!checkIsLoaded(docTypesLoading)) dispatch(getDocTypes());
    if (!checkIsLoaded(phoneTypesLoading)) dispatch(fetchPhoneTypes());
    if (!checkIsLoaded(accountTypesLoading)) dispatch(fetchAccountTypes());
  }, []);

  const { isLoading, startLoading, stopLoading } = useLoading();

  const createOrganizationAccount = ({ params, callback }) => {
    // console.log(params);
    startLoading();
    const organizationProperties: AnyObject[] = [];
    const commonProperties: AnyObject[] = [];
    const subscribers = [];
    const responsibleUser: { id: number }[] = [];
    const contactIndividual: AnyObject[] = [];
    const legalAddress = {
      countryId: params?.country,
      regionId: params?.region,
      postCode: params?.index || "",
      district: params?.district,
      city: params?.city,
      village: params?.village,
      street: params?.street,
      house: params?.house,
      building: params?.building,
      apartment: params?.apartment,
      unstructuredAddress: params?.unstructuredAddress,
    };
    const actualAddress = params.isSame
      ? legalAddress
      : {
          countryId: params?.actualCountry,
          regionId: params?.actualRegion,
          postCode: params?.actualIndex || "",
          district: params?.actualDistrict,
          city: params?.actualCity,
          village: params?.actualVillage,
          street: params?.actualStreet,
          house: params?.actualHouse,
          building: params?.actualBuilding,
          apartment: params?.actualApartment,
          unstructuredAddress: params?.actualUnstructuredAddress,
        };

    if (hasLocalAccess("account.create.exiteId")) {
      params.exiteId.forEach((id) => {
        if (id !== "") {
          // @ts-ignore
          subscribers.push({ externalId: id, subscriberTypeId: 1 });
        }
      });
    }

    if (hasLocalAccess("account.create.requisites")) {
      neededProperties.forEach((typeId) => {
        organizationProperties.push({
          propertyType: { alias: typeId },
          stringValue: params[typeId],
        });
      });
    }
    if (hasLocalAccess("account.create.accountType")) {
      commonProperties.push({ typeId: "account_type", dictionaryValue: params.accountType });
    }
    if (hasLocalAccess("account.create.timezone") && params.timezone) {
      commonProperties.push({ typeId: "timezone", stringValue: params.timezone });
    }

    if (hasLocalAccess("account.create.responsibleUsers") && params?.responsibleUser) {
      params.responsibleUser.forEach(({ id }) => {
        responsibleUser.push({ id });
      });
    }

    if (hasLocalAccess("account.create.contactIndividual") && params?.contactIndividual) {
      params.contactIndividual.forEach((item) => {
        contactIndividual.push({
          sex: item.sex,
          firstName: item.firstName,
          middleName: item.middleName,
          lastName: item.lastName,
          emails: item.emails.map((email) => ({
            email,
          })),
          phones: item.phones.map((phone) => phone),
          actualAddress: {
            countryId: item.countryId,
            regionId: item.regionId,
            unstructuredAddress: item.unstructuredAddress,
            postCode: "",
          },
          properties: [
            {
              typeId: "position",
              stringValue: item.position,
            },
          ],
        });
      });
    }

    const queryParams = {
      accountStatusId: params.status,
      accTypeId: params.accTypeId,
      fromDate: toHttpFormatStartOfDay(params.createDate),
      subscribers,
      organization: {
        shortName: params?.shortName,
        fullName: params?.fullName,
        legalAddress,
        actualAddress,
        phones: params.phones,
        properties: organizationProperties,
        contactIndividual,
      },
      billDeliveryEmails: params?.email.flatMap((email) =>
        email !== ""
          ? {
              email,
            }
          : []
      ),
    };

    // return console.log({ queryParams });
    axios
      .post(createAccountRoute(), queryParams)
      .then(({ data }) => {
        callback();
        message.success("Аккаунт создан");
        history.push(`/accounts/${data.id}/info`);
      })
      .catch((error) => {
        message.error(error?.response?.data?.message);
      })
      .finally(stopLoading);
  };

  if (
    checkIsLoaded(
      accountStatusesLoading,
      countriesLoading,
      regionsLoading,
      organizationPropertyTypesLoading,
      docTypesLoading,
      tenantPropertiesLoading,
      phoneTypesLoading,
      accountTypesLoading
    )
  ) {
    return (
      <div className="container_height" style={{ padding: 10 }}>
        <AccountCreate
          createOrganizationAccount={createOrganizationAccount}
          neededProperties={neededProperties}
          isCreateInProcess={isLoading}
        />
      </div>
    );
  }
  return (
    <div className="container_height">
      <Spin className="spin" />
    </div>
  );
}

export default AccountCreateContainer;
